import React, { useEffect, useMemo, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { SearchSelect, renderFieldInputForPreset } from "./FieldComponents";

const SUPPORTED_TARGET_FIELD_TYPES = new Set([1, 2, 3, 4, 5, 6, 7, 10, 11, 12, 22]);

const isEmptyValue = (value) => {
    if (value === null || value === undefined || value === '') return true;
    if (Array.isArray(value) && value.length === 0) return true;
    return false;
};

const parsePresetValue = (field, value) => {
    if (!field) return value;
    if (value === null || value === undefined) return value;

    if (field.field_id === 11) {
        if (Array.isArray(value)) return value;
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    }

    if (field.field_id === 12) {
        if (value === '' || value === null || value === undefined) return 2;
        return Number(value);
    }

    return value;
};

const serializePresetValue = (field, value) => {
    if (!field) return value;
    if (field.field_id === 11) {
        if (Array.isArray(value)) {
            return JSON.stringify(value);
        }
    }
    return value;
};

const normalizeSectionIds = (sectionId) => {
    if (sectionId === null || sectionId === undefined || sectionId === '') return [];
    if (Array.isArray(sectionId)) return sectionId.map((id) => Number(id)).filter(Boolean);

    if (typeof sectionId === 'string') {
        try {
            const parsed = JSON.parse(sectionId);
            if (Array.isArray(parsed)) {
                return parsed.map((id) => Number(id)).filter(Boolean);
            }
        } catch (e) {
            if (sectionId.includes(',')) {
                return sectionId
                    .split(',')
                    .map((id) => Number(id.trim()))
                    .filter(Boolean);
            }
        }
    }

    const numeric = Number(sectionId);
    return Number.isFinite(numeric) && numeric > 0 ? [numeric] : [];
};

const buildSearchOptions = (fields, sections) => {
    return Object.fromEntries(
        fields.map((field) => {
            const sectionIds = normalizeSectionIds(field.section_id);
            const sectionName = sectionIds
                .map((id) => sections[id])
                .filter(Boolean)
                .join(', ') || 'Section';
            return [String(field.id), `${field.name} (${sectionName})`];
        })
    );
};

const renderPresetValueInput = ({ field, value, onChange }) => {
    return renderFieldInputForPreset({ field, value, onChange });
};

export const ContactFieldPresets = ({ contactId = null, presets, setPresets, setFieldMap }) => {
    const [sourceFields, setSourceFields] = useState([]);
    const [fieldsBySection, setFieldsBySection] = useState({});
    const [sections, setSections] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchOptions = async () => {
            setIsLoading(true);
            try {
                const url = contactId
                    ? `https://api.casedb.co/contact_field_presets.php?contact_id=${contactId}`
                    : 'https://api.casedb.co/contact_field_presets.php';
                const response = await fetch(url, { credentials: 'include' });
                const data = await response.json();
                if (data.success) {
                    setSourceFields(data.source_fields || []);
                    setFieldsBySection(data.fields_by_section || {});
                    setSections(data.sections || {});

                    const fieldsMap = {};
                    Object.values(data.fields_by_section || {}).forEach((sectionFields) => {
                        sectionFields.forEach((field) => {
                            fieldsMap[field.id] = field;
                        });
                    });
                    if (typeof setFieldMap === 'function') {
                        setFieldMap(fieldsMap);
                    }

                    if (data.presets && Array.isArray(data.presets) && setPresets) {
                        setPresets(data.presets.map((preset) => ({
                            source_field_id: preset.source_field_id,
                            target_field_id: preset.target_field_id,
                            target_value: preset.target_value
                        })));
                    }
                }
            } catch (error) {
                console.error('Failed to load contact field preset options', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchOptions();
    }, [contactId, setPresets, setFieldMap]);

    const sourceOptions = useMemo(() => buildSearchOptions(sourceFields, sections), [sourceFields, sections]);

    const fieldById = useMemo(() => {
        const map = {};
        Object.values(fieldsBySection).forEach((sectionFields) => {
            sectionFields.forEach((field) => {
                map[field.id] = field;
            });
        });
        return map;
    }, [fieldsBySection]);

    const addPresetRow = () => {
        setPresets([
            ...presets,
            { source_field_id: null, target_field_id: null, target_value: '' }
        ]);
    };

    const updatePreset = (index, updates) => {
        const next = [...presets];
        next[index] = { ...next[index], ...updates };
        setPresets(next);
    };

    const removePreset = (index) => {
        const next = [...presets];
        next.splice(index, 1);
        setPresets(next);
    };

    return (
        <div className="contact-presets">
            <div className="sub-title">
                <h4>Contact Field Defaults</h4>
            </div>
            <p className="small-text" style={{ margin: '0' }}>
                When this contact is selected in a contact field within an add-item section, the selected target field will be filled automatically if it is empty.
            </p>

            {isLoading && <div className="subtext">Loading preset options…</div>}

            {!isLoading && presets.length === 0 && (
                <div className="subtext">No presets yet. Add one below.</div>
            )}

            {presets.map((preset, index) => {
                const sourceField = preset.source_field_id ? fieldById[preset.source_field_id] : null;
                const sourceSectionIds = normalizeSectionIds(sourceField?.section_id);
                const allFields = Object.values(fieldById);
                const targetFields = sourceSectionIds.length > 0
                    ? allFields.filter((field) => {
                        if (!SUPPORTED_TARGET_FIELD_TYPES.has(field.field_id)) return false;
                        const fieldSectionIds = normalizeSectionIds(field.section_id);
                        return fieldSectionIds.some((id) => sourceSectionIds.includes(id));
                    })
                    : [];
                const targetOptions = buildSearchOptions(targetFields, sections);
                const targetField = preset.target_field_id ? fieldById[preset.target_field_id] : null;
                const parsedValue = parsePresetValue(targetField, preset.target_value);

                return (
                    <div key={`preset-${index}`} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '12px', alignItems: 'center' }}>
                        <div>
                            <label className="subtext">Contact Field</label>
                            <SearchSelect
                                value={preset.source_field_id}
                                onChange={(val) => updatePreset(index, { source_field_id: Number(val) || null, target_field_id: null, target_value: '' })}
                                options={sourceOptions}
                                placeholder="Select contact field"
                            />
                        </div>
                        <div>
                            <label className="subtext">Target Field</label>
                            <SearchSelect
                                value={preset.target_field_id}
                                onChange={(val) => updatePreset(index, { target_field_id: Number(val) || null, target_value: '' })}
                                options={targetOptions}
                                placeholder={sourceField ? 'Select target field' : 'Pick contact field first'}
                                disabled={!sourceField}
                            />
                        </div>
                        <div className="form-group nm">
                            <label className="subtext">Target Value</label>
                            {targetField ? (
                                renderPresetValueInput({
                                    field: targetField,
                                    value: parsedValue,
                                    onChange: (val) => updatePreset(index, { target_value: serializePresetValue(targetField, val) })
                                })
                            ) : (
                                <div className="subtext">Select a target field</div>
                            )}
                        </div>
                        <button type="button" className="form-box alt" title="Remove preset" onClick={() => removePreset(index)}>
                            <Minus size={14} />
                        </button>
                    </div>
                );
            })}

            <div className="form-group small">
                <button type="button" className="action alt" onClick={addPresetRow}>
                    <Plus size={14} /> Add preset
                </button>
            </div>
        </div>
    );
};
